import { Card, Skeleton, Tabs, Tag } from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
import ChartComponent from "./Chart";

const { TabPane } = Tabs;

// ── Review count badge ────────────────────────────────
const ReviewBadge = ({ value, color, bg, border }) => (
    <span
        style={{
            display: "inline-block",
            background: bg,
            border: `1px solid ${border}`,
            color,
            borderRadius: 6,
            padding: "2px 10px",
            fontWeight: 600,
            fontSize: 12,
            minWidth: 36,
            textAlign: "center",
        }}
    >
        {value ?? "—"}
    </span>
);

// ── Shared review columns ─────────────────────────────
const reviewColumns = [
    {
        title: "Total",
        dataIndex: "count",
        key: "count",
        render: (v) => (
            <span
                style={{
                    color: "var(--color-text)",
                    fontWeight: 600,
                    fontSize: 13,
                }}
            >
                {v ?? "—"}
            </span>
        ),
    },
    {
        title: "Excellent",
        dataIndex: "excellent_reviews",
        key: "excellent_reviews",
        render: (v) => (
            <ReviewBadge
                value={v}
                color="var(--color-success)"
                bg="var(--color-success-light)"
                border="var(--color-success)"
            />
        ),
    },
    {
        title: "Good",
        dataIndex: "good_reviews",
        key: "good_reviews",
        render: (v) => (
            <ReviewBadge
                value={v}
                color="var(--color-primary-hover)"
                bg="var(--color-primary-light)"
                border="var(--color-primary-hover)"
            />
        ),
    },
    {
        title: "Poor",
        dataIndex: "poor_reviews",
        key: "poor_reviews",
        render: (v) => (
            <ReviewBadge
                value={v}
                color="var(--color-error)"
                bg="var(--color-error-light)"
                border="var(--color-error)"
            />
        ),
    },
];

const genderColumn = [
    {
        title: "Gender",
        dataIndex: "gender",
        key: "gender",
        render: (v) => (
            <Tag
                style={{
                    background:
                        v?.toLowerCase() === "male"
                            ? "var(--color-primary-light)"
                            : "var(--color-error-light)",
                    border: `1px solid ${
                        v?.toLowerCase() === "male"
                            ? "var(--color-primary-hover)"
                            : "var(--color-error)"
                    }`,
                    color:
                        v?.toLowerCase() === "male"
                            ? "var(--color-primary-hover)"
                            : "var(--color-error)",
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12,
                }}
            >
                {v}
            </Tag>
        ),
    },
    ...reviewColumns,
];

const ageColumn = [
    {
        title: "Age Group",
        dataIndex: "age_group",
        key: "age_group",
        render: (v) => (
            <span
                style={{
                    display: "inline-block",
                    background: "var(--color-bg-elevated)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-primary)",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontWeight: 600,
                    fontSize: 12,
                }}
            >
                {v}
            </span>
        ),
    },
    ...reviewColumns,
];

const stateColumn = [
    {
        title: "State",
        dataIndex: "state",
        key: "state",
        render: (v) => (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "var(--color-accent)",
                        flexShrink: 0,
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontSize: 13,
                        fontWeight: 500,
                    }}
                >
                    {v}
                </span>
            </div>
        ),
    },
    ...reviewColumns,
];

function DemoGraphic() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { selectedHotels } = useContext(SettingContext);
    const hotel_ids = selectedHotels;

    const [selectedTab, setSelectedTab] = useState("gender");
    const [genderData, setGenderData] = useState([]);
    const [ageData, setAgeData] = useState([]);
    const [stateData, setStateData] = useState([]);

    const [genderFormData, setGenderFormData] = useState({
        staff_id,
        hotel_ids,
        all_selected: hotel_ids.length === 0,
        data_for: "gender",
    });
    const [ageFormData, setAgeFormData] = useState({
        staff_id,
        hotel_ids,
        all_selected: hotel_ids.length === 0,
        data_for: "age_group",
    });
    const [stateFormData, setStateFormData] = useState({
        staff_id,
        hotel_ids,
        all_selected: hotel_ids.length === 0,
        data_for: "states",
    });

    const mkQuery = (key, formData) =>
        useQuery(
            key,
            async () =>
                await HTTP("/analytics/getDemographicTableData", formData),
            { enabled: false },
        );

    const {
        status: genderDataStatus,
        data: genderRes,
        refetch: getGenderData,
    } = mkQuery("gender-analytics", genderFormData);
    const {
        status: ageDataStatus,
        data: ageRes,
        refetch: getAgeData,
    } = mkQuery("age-analytics", ageFormData);
    const {
        status: stateDataStatus,
        data: stateRes,
        refetch: getStateData,
    } = mkQuery("state-analytics", stateFormData);

    useEffect(() => {
        getGenderData();
    }, []);

    useEffect(() => {
        const ids = selectedHotels;
        if (selectedTab === "gender")
            setGenderFormData((p) => ({
                ...p,
                page: 0,
                hotel_ids: ids,
                all_selected: ids.length === 0,
            }));
        else if (selectedTab === "age")
            setAgeFormData((p) => ({
                ...p,
                page: 0,
                hotel_ids: ids,
                all_selected: ids.length === 0,
            }));
        else
            setStateFormData((p) => ({
                ...p,
                page: 0,
                hotel_ids: ids,
                all_selected: ids.length === 0,
            }));
    }, [selectedHotels]);

    useEffect(() => {
        getAgeData();
    }, [ageFormData]);
    useEffect(() => {
        getStateData();
    }, [stateFormData]);
    useEffect(() => {
        getGenderData();
    }, [genderFormData]);

    useEffect(() => {
        if (genderDataStatus === "success") setGenderData(genderRes.data);
    }, [genderDataStatus, genderRes]);
    useEffect(() => {
        if (ageDataStatus === "success") setAgeData(ageRes.data);
    }, [ageDataStatus, ageRes]);
    useEffect(() => {
        if (stateDataStatus === "success") setStateData(stateRes.data);
    }, [stateDataStatus, stateRes]);

    function changeView(key) {
        const ids = selectedHotels;
        if (key === "gender")
            setGenderFormData((p) => ({
                ...p,
                hotel_ids: ids,
                all_selected: ids.length === 0,
            }));
        else if (key === "age")
            setAgeFormData((p) => ({
                ...p,
                hotel_ids: ids,
                all_selected: ids.length === 0,
            }));
        else if (key === "state")
            setStateFormData((p) => ({
                ...p,
                hotel_ids: ids,
                all_selected: ids.length === 0,
            }));
        setSelectedTab(key);
    }

    const isReady = (s) => s !== "loading" && s !== "idle";

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            <Card>
                <Tabs type="card" onChange={changeView}>
                    <TabPane tab="Gender" key="gender">
                        {isReady(genderDataStatus) ? (
                            <CustomTable
                                columns={genderColumn}
                                tableData={genderData}
                                rowKey="id"
                                isScroll
                            />
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10 }}
                            />
                        )}
                    </TabPane>
                    <TabPane tab="Age Group" key="age">
                        {isReady(ageDataStatus) ? (
                            <CustomTable
                                columns={ageColumn}
                                tableData={ageData}
                                rowKey="id"
                                isScroll
                            />
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10 }}
                            />
                        )}
                    </TabPane>
                    <TabPane tab="State" key="state">
                        {isReady(stateDataStatus) ? (
                            <CustomTable
                                columns={stateColumn}
                                tableData={stateData}
                                rowKey="id"
                                isScroll
                            />
                        ) : (
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 10 }}
                            />
                        )}
                    </TabPane>
                    <TabPane tab="Chart View" key="chart">
                        <ChartComponent />
                    </TabPane>
                </Tabs>
            </Card>
        </Card>
    );
}

export { DemoGraphic };
